import { useState } from 'react';
import { AlertCircle, CheckCircle2, ShieldCheck, ShieldOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  useTwoFAStatus,
  useStart2FASetup,
  useVerify2FASetup,
  useDisable2FA,
  type ProfileMe,
  type TwoFASetup,
} from '@/lib/api/profile';
import { ApiError } from '@/lib/api';

/**
 * Раздел «2FA» — управление TOTP-двухфакторкой.
 *
 * Backend (`/auth/2fa/*`):
 *   - GET /status — { enabled, has_secret, recovery_codes_left }
 *   - POST /setup — { secret, qr_data_url, provisioning_uri }
 *   - POST /verify-setup — { code } → { enabled, recovery_codes: string[] }
 *   - POST /disable — { code } → 204
 *
 * Disable принимает ТОЛЬКО TOTP-код (НЕ пароль) — см. backend twofa.py.
 *
 * Flow:
 *   1. enabled=false → кнопка «Включить» → mutate setup → отрисуем QR + поле кода
 *   2. ввод 6-значного кода → verify-setup → показываем recovery codes (1 раз!)
 *   3. enabled=true → бейдж + кнопка «Выключить» → запрос кода → disable
 */

interface SectionProps {
  me: ProfileMe;
}

export function ProfileSectionTwoFA({ me }: SectionProps) {
  const statusQuery = useTwoFAStatus();
  const startMut = useStart2FASetup();
  const verifyMut = useVerify2FASetup();
  const disableMut = useDisable2FA();

  const [mode, setMode] = useState<'idle' | 'setup' | 'disable'>('idle');
  const [setupData, setSetupData] = useState<TwoFASetup | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const enabled = statusQuery.data?.enabled ?? me.two_factor_enabled;
  const codesLeft = statusQuery.data?.recovery_codes_left;

  const startSetup = async () => {
    setError(null);
    try {
      const data = await startMut.mutateAsync();
      setSetupData(data);
      setCode('');
      setMode('setup');
    } catch (err) {
      if (err instanceof ApiError) setError(err.detail);
      else setError('Не удалось запустить настройку');
    }
  };

  const verifySetup = async () => {
    setError(null);
    if (!/^\d{6}$/.test(code)) {
      setError('Введите 6-значный код из приложения');
      return;
    }
    try {
      const out = await verifyMut.mutateAsync({ code });
      setRecoveryCodes(out.recovery_codes);
      setSetupData(null);
      setCode('');
      setMode('idle');
    } catch (err) {
      if (err instanceof ApiError) setError(err.detail);
      else setError('Не удалось проверить код');
    }
  };

  const submitDisable = async () => {
    setError(null);
    if (!/^\d{6}$/.test(code)) {
      setError('Введите 6-значный код из приложения');
      return;
    }
    try {
      await disableMut.mutateAsync({ code });
      setCode('');
      setMode('idle');
    } catch (err) {
      if (err instanceof ApiError) setError(err.detail);
      else setError('Не удалось отключить 2FA');
    }
  };

  if (statusQuery.isLoading) {
    return <Skeleton className="h-20 w-full" />;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        {enabled ? (
          <Badge variant="primary" className="gap-1">
            <ShieldCheck className="h-3 w-3" />
            Включена
          </Badge>
        ) : (
          <Badge variant="secondary" className="gap-1">
            <ShieldOff className="h-3 w-3" />
            Отключена
          </Badge>
        )}
        {enabled && typeof codesLeft === 'number' && (
          <span className="text-xs text-muted-foreground">
            Кодов восстановления: {codesLeft}
          </span>
        )}
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {recoveryCodes && (
        <Alert>
          <CheckCircle2 className="h-4 w-4" />
          <AlertDescription>
            <p className="font-medium mb-1">2FA включена. Сохраните коды восстановления:</p>
            <pre className="text-xs bg-secondary/60 p-2 rounded font-mono whitespace-pre-wrap">
              {recoveryCodes.join('\n')}
            </pre>
            <p className="text-xs text-muted-foreground mt-1">
              Каждый код одноразовый. Больше эти коды не показываются.
            </p>
          </AlertDescription>
        </Alert>
      )}

      {!enabled && mode === 'idle' && (
        <Button
          type="button"
          size="sm"
          onClick={startSetup}
          disabled={startMut.isPending}
        >
          <ShieldCheck className="h-4 w-4 mr-2" />
          {startMut.isPending ? 'Запускаем…' : 'Включить 2FA'}
        </Button>
      )}

      {!enabled && mode === 'setup' && setupData && (
        <div className="space-y-3">
          <p className="text-xs text-muted-foreground">
            1) Отсканируйте QR в приложении (Google Authenticator, Authy, 1Password).
          </p>
          <div className="bg-white p-2 rounded border border-border flex items-center justify-center">
            <img
              src={setupData.qr_data_url}
              alt="QR код для 2FA"
              className="w-40 h-40"
            />
          </div>
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground">
              Не получается отсканировать? Введите секрет вручную
            </summary>
            <code className="block mt-1 bg-secondary/60 p-1.5 rounded font-mono break-all">
              {setupData.secret}
            </code>
          </details>
          <div className="space-y-1.5">
            <Label htmlFor="twofa-code">2) Введите 6-значный код из приложения</Label>
            <Input
              id="twofa-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            />
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              onClick={verifySetup}
              disabled={verifyMut.isPending}
              className="flex-1"
            >
              {verifyMut.isPending ? 'Проверяем…' : 'Подтвердить'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setMode('idle');
                setSetupData(null);
                setCode('');
                setError(null);
              }}
            >
              Отмена
            </Button>
          </div>
        </div>
      )}

      {enabled && mode === 'idle' && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            setMode('disable');
            setCode('');
            setError(null);
            setRecoveryCodes(null);
          }}
        >
          <ShieldOff className="h-4 w-4 mr-2" />
          Выключить 2FA
        </Button>
      )}

      {enabled && mode === 'disable' && (
        <div className="space-y-2">
          <div className="space-y-1.5">
            <Label htmlFor="twofa-disable-code">Код из приложения</Label>
            <Input
              id="twofa-disable-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            />
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="destructive"
              size="sm"
              onClick={submitDisable}
              disabled={disableMut.isPending}
              className="flex-1"
            >
              {disableMut.isPending ? 'Отключаем…' : 'Подтвердить отключение'}
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => {
                setMode('idle');
                setCode('');
                setError(null);
              }}
            >
              Отмена
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
